import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Target, Copy, Palette, Check, Sparkles } from 'lucide-react';
import {
  getMyDonateGoal,
  saveDonateGoal,
  DonateGoal,
} from '../../../api/payment/donate-goal.api';
import { parseServerDate } from '../../../utils/server-date';
import DonateGoalWidget from '../../../components/donate-goal/donate-goal-widget';
import {
  DONATE_GOAL_PRESETS,
  DonateGoalPreset,
  DonateGoalTheme,
  parseDonateGoalTheme,
  resolveDonateGoalTheme,
  serializeDonateGoalTheme,
} from '../../../components/donate-goal/donate-goal-theme';

// Nền giả lập khung hình live để thấy rõ thẻ kính mờ / trong suốt trông ra sao khi đè lên video.
const STAGE_BG =
  'radial-gradient(circle at 18% 28%, rgba(251,146,60,.55), rgba(251,146,60,0) 42%),' +
  'radial-gradient(circle at 82% 72%, rgba(20,184,166,.5), rgba(20,184,166,0) 45%),' +
  'radial-gradient(circle at 60% 10%, rgba(244,114,182,.35), rgba(244,114,182,0) 40%),' +
  'linear-gradient(135deg, #0B1220 0%, #1F2937 100%)';

const SAMPLE = {
  title: 'Mua micro mới',
  description: 'Cảm ơn mọi người đã ủng hộ',
  currentAmount: 320000,
  targetAmount: 500000,
  donationCount: 128,
};

interface ColorFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (hex: string) => void;
}

// Khai báo ngoài component chính để không bị dựng lại (mất focus) mỗi lần gõ.
const ColorField: React.FC<ColorFieldProps> = ({ id, label, value, onChange }) => (
  <label
    htmlFor={id}
    className="flex min-w-0 items-center gap-2 rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-700 px-2 py-1.5 cursor-pointer"
  >
    <input
      id={id}
      type="color"
      value={value.toLowerCase()}
      onChange={(e) => onChange(e.target.value.toUpperCase())}
      className="h-8 w-10 shrink-0 cursor-pointer rounded border-0 bg-transparent p-0"
    />
    <span className="min-w-0 truncate text-sm text-gray-700 dark:text-gray-200">{label}</span>
    <span className="ml-auto shrink-0 font-mono text-xs uppercase text-gray-500 dark:text-gray-400">
      {value}
    </span>
  </label>
);

const useIsNarrow = () => {
  const get = () => typeof window !== 'undefined' && window.innerWidth < 520;
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    const onResize = () => setNarrow(get());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return narrow;
};

/**
 * "Mục tiêu donate": streamer đặt mục tiêu (vd 500.000 Ca mua micro) rồi chèn
 * thanh tiến độ lên phiên live bằng link overlay (OBS Browser Source).
 * Tiến độ do BE tính lại từ giao dịch thật nên luôn khớp, không cần đồng bộ tay.
 */
const DonateGoalSetting: React.FC<{ userId?: string }> = ({ userId }) => {
  const { host, protocol } = window.location;
  const overlayUrl = `${protocol}//${host}/donate-goal-screen/${userId}`;

  const [goal, setGoal] = useState<DonateGoal | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [targetAmount, setTargetAmount] = useState<string>('');
  const [endAt, setEndAt] = useState<string>('');
  const [isActive, setIsActive] = useState(true);
  const [resetProgress, setResetProgress] = useState(false);
  const [saving, setSaving] = useState(false);
  const [theme, setTheme] = useState<DonateGoalTheme>({});
  // Bấm "Đặt lại mặc định" mà chưa chỉnh gì thêm thì gửi "" để BE xoá giao diện đã lưu.
  const [themeReset, setThemeReset] = useState(false);
  const [demoTick, setDemoTick] = useState(0);
  const [demoBonus, setDemoBonus] = useState(0);
  const [demoCount, setDemoCount] = useState(0);
  const isNarrow = useIsNarrow();

  useEffect(() => {
    getMyDonateGoal()
      .then((res) => {
        setGoal(res);
        setTheme(parseDonateGoalTheme(res?.theme));
        if (res?.exists) {
          setTitle(res.title || '');
          setDescription(res.description || '');
          setTargetAmount(`${res.targetAmount || ''}`);
          setIsActive(!!res.isActive);
          // datetime-local cần dạng yyyy-MM-ddTHH:mm (giờ máy người dùng).
          if (res.endAt) {
            const d = parseServerDate(res.endAt); // BE trả UTC không kèm 'Z'
            if (d) {
              const pad = (n: number) => `${n}`.padStart(2, '0');
              setEndAt(
                `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
                  d.getHours()
                )}:${pad(d.getMinutes())}`
              );
            }
          }
        }
      })
      .catch(() => {
        /* chưa có mục tiêu hoặc lỗi mạng -> để form trống */
      });
  }, []);

  const handleSave = () => {
    if (saving) return; // chặn bấm 2 lần
    const amount = Number(targetAmount);
    if (!title.trim()) {
      toast.error('Vui lòng nhập tên mục tiêu.');
      return;
    }
    if (!amount || amount <= 0) {
      toast.error('Số Ca mục tiêu phải lớn hơn 0.');
      return;
    }
    setSaving(true);
    saveDonateGoal({
      title: title.trim(),
      targetAmount: amount,
      description: description.trim() || null,
      endAt: endAt ? new Date(endAt).toISOString() : null,
      isActive,
      resetProgress,
      theme: themeReset ? '' : serializeDonateGoalTheme(theme),
    })
      .then((res) => {
        setGoal(res);
        setResetProgress(false);
        setThemeReset(false);
        toast.success('Đã lưu mục tiêu donate');
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Lưu mục tiêu thất bại');
      })
      .finally(() => setSaving(false));
  };

  // Mở overlay để căn vị trí trong OBS.
  const openPreview = () => {
    window.open(overlayUrl, 'popup', 'width=700,height=320,top=100,left=100');
  };

  const copyOverlay = async () => {
    try {
      await navigator.clipboard.writeText(overlayUrl);
      toast.success('Đã sao chép link overlay');
    } catch {
      toast.error('Trình duyệt chặn sao chép — hãy bôi đen link rồi copy tay.');
    }
  };

  const resolved = resolveDonateGoalTheme(theme);

  const pickPreset = (preset: DonateGoalPreset) => {
    setTheme({ preset }); // chọn mẫu thì bỏ các màu tự chỉnh trước đó
    setThemeReset(false);
  };

  const patchTheme = (patch: Partial<DonateGoalTheme>) => {
    setTheme((prev) => ({ ...prev, preset: resolved.preset, ...patch }));
    setThemeReset(false);
  };

  const resetTheme = () => {
    setTheme({});
    setThemeReset(true);
  };

  // Xem trước dùng số thật của mục tiêu (hoặc số mẫu khi chưa có), tên/mô tả lấy theo ô đang gõ.
  const hasGoal = !!goal?.exists;
  const typedTarget = Number(targetAmount);
  const previewTarget =
    typedTarget > 0 ? typedTarget : hasGoal ? goal?.targetAmount || 0 : SAMPLE.targetAmount;
  const previewBase = hasGoal ? goal?.currentAmount || 0 : SAMPLE.currentAmount;
  const previewCurrent = previewBase + demoBonus;
  const previewCount = (hasGoal ? goal?.donationCount || 0 : SAMPLE.donationCount) + demoCount;
  const previewTitle = title.trim() || (hasGoal ? goal?.title || '' : SAMPLE.title);
  const previewDesc = title.trim() || hasGoal ? description.trim() : SAMPLE.description;

  // Giả lập một lượt donate ngay trên bản xem trước, không gửi gì lên máy chủ.
  const simulateDonate = () => {
    if (previewTarget > 0 && previewCurrent >= previewTarget) {
      setDemoBonus(0);
      setDemoCount(0);
      return;
    }
    setDemoBonus((b) => b + Math.max(1, Math.round(previewTarget * 0.12)));
    setDemoCount((c) => c + 1);
    setDemoTick((t) => t + 1);
  };

  return (
    <div className="border rounded-xl border-[#DEE0E6] dark:border-slate-700 mt-[10px]">
      <p className="p-3 flex items-center gap-2 w-full text-[#18191E] dark:text-white font-semibold text-[22px] leading-[27px]">
        <Target size={20} className="text-blue-600" />
        Mục tiêu donate
      </p>

      <div className="w-full flex flex-col gap-3 bg-[#f6f7f8] dark:bg-dark2 p-3">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Đặt mục tiêu (vd “Mua micro mới”) rồi chèn thanh tiến độ lên phiên live. Tiến độ
          tự cộng theo số Ca bạn nhận được, không cần cập nhật tay.
        </p>

        <div className="flex flex-col gap-1">
          <label htmlFor="goal-title" className="text-sm">
            Tên mục tiêu
          </label>
          <input
            id="goal-title"
            value={title}
            maxLength={80}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="VD: Mua micro mới"
            className="h-12 w-full rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-700 px-3 text-base dark:text-white"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="goal-desc" className="text-sm">
            Mô tả (không bắt buộc)
          </label>
          <input
            id="goal-desc"
            value={description}
            maxLength={100}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Ví dụ: Gom tiền mua mic mới cho buổi live cuối tuần"
            className="h-12 w-full rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-700 px-3 text-base dark:text-white"
          />
          <span className="self-end text-xs text-gray-500 dark:text-gray-400">
            {description.length}/100
          </span>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="goal-target" className="text-sm">
            Số Ca mục tiêu
          </label>
          <input
            id="goal-target"
            type="number"
            min={1}
            value={targetAmount}
            onChange={(e) => setTargetAmount(e.target.value)}
            placeholder="VD: 500000"
            className="h-12 w-full rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-700 px-3 text-base dark:text-white"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="goal-end" className="text-sm">
            Hạn chót (không bắt buộc)
          </label>
          <input
            id="goal-end"
            type="datetime-local"
            value={endAt}
            onChange={(e) => setEndAt(e.target.value)}
            className="h-12 w-full rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-white dark:bg-slate-700 px-3 text-base dark:text-white"
          />
        </div>

        <label className="flex items-start gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="mt-[3px] h-4 w-4 shrink-0 rounded border-[#C8CCD8] accent-blue-600"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Đang chạy
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              Tắt thì overlay không hiện gì, mục tiêu vẫn được giữ lại.
            </span>
          </span>
        </label>

        {goal?.exists && (
          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={resetProgress}
              onChange={(e) => setResetProgress(e.target.checked)}
              className="mt-[3px] h-4 w-4 shrink-0 rounded border-[#C8CCD8] accent-blue-600"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Bắt đầu lại từ 0
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Tính tiến độ từ lúc lưu. Các lượt donate trước đó không còn được cộng vào
                mục tiêu này.
              </span>
            </span>
          </label>
        )}

        <div className="flex flex-col gap-3 rounded-xl border border-[#DEE0E6] dark:border-slate-700 bg-white dark:bg-slate-800 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 text-base font-semibold text-[#18191E] dark:text-white">
              <Palette size={18} className="text-blue-600" />
              Giao diện trên live
            </h3>
            <button
              type="button"
              onClick={resetTheme}
              className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              Đặt lại mặc định
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Chọn một mẫu rồi chỉnh màu theo ý. Khung xem trước bên dưới hiện đúng như thanh trên
            live.
          </p>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {DONATE_GOAL_PRESETS.map((p) => {
              const selected = resolved.preset === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  aria-pressed={selected}
                  aria-label={`Mẫu ${p.label}`}
                  onClick={() => pickPreset(p.id)}
                  className={`overflow-hidden rounded-xl border-2 text-left transition ${
                    selected
                      ? 'border-blue-600 ring-2 ring-blue-600/20'
                      : 'border-transparent hover:border-gray-300 dark:hover:border-slate-500'
                  }`}
                >
                  <div className="pointer-events-none p-2.5" style={{ background: STAGE_BG }}>
                    <DonateGoalWidget size="mini" theme={{ preset: p.id }} {...SAMPLE} />
                  </div>
                  <div className="flex items-center justify-between bg-[#f6f7f8] dark:bg-slate-700 px-2.5 py-1.5">
                    <span
                      className={`text-sm font-medium ${
                        selected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-200'
                      }`}
                    >
                      {p.label}
                    </span>
                    {selected && <Check size={16} className="text-blue-600 dark:text-blue-400" />}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <span className="text-sm text-gray-700 dark:text-gray-200">Màu thanh</span>
              <div
                className="h-2 w-full rounded-full"
                style={{
                  background: `linear-gradient(90deg, ${resolved.barFrom}, ${resolved.barTo})`,
                }}
              />
              <div className="grid grid-cols-2 gap-2">
                <ColorField
                  id="goal-bar-from"
                  label="Đầu"
                  value={resolved.barFrom}
                  onChange={(barFrom) => patchTheme({ barFrom })}
                />
                <ColorField
                  id="goal-bar-to"
                  label="Cuối"
                  value={resolved.barTo}
                  onChange={(barTo) => patchTheme({ barTo })}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-sm text-gray-700 dark:text-gray-200">Màu nền</span>
              <ColorField
                id="goal-bg"
                label="Nền thẻ"
                value={resolved.bg}
                onChange={(bg) => patchTheme({ bg })}
              />
              <div className="flex items-center gap-3">
                <label
                  htmlFor="goal-bg-opacity"
                  className="shrink-0 text-xs text-gray-600 dark:text-gray-300"
                >
                  Độ mờ nền
                </label>
                <input
                  id="goal-bg-opacity"
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={resolved.bgOpacity}
                  onChange={(e) => patchTheme({ bgOpacity: Number(e.target.value) })}
                  className="min-w-0 flex-1 accent-blue-600"
                />
                <span className="w-10 shrink-0 text-right text-xs tabular-nums text-gray-600 dark:text-gray-300">
                  {resolved.bgOpacity}%
                </span>
              </div>
            </div>

            <ColorField
              id="goal-text-color"
              label="Màu chữ"
              value={resolved.text}
              onChange={(text) => patchTheme({ text })}
            />
            <ColorField
              id="goal-accent-color"
              label="Màu số tiền"
              value={resolved.accent}
              onChange={(accent) => patchTheme({ accent })}
            />
          </div>

          <div
            className="overflow-hidden rounded-xl px-3 py-5 sm:px-6 sm:py-8"
            style={{ background: STAGE_BG }}
            data-testid="donate-goal-preview"
          >
            <div className="mx-auto w-full max-w-[560px]">
              <DonateGoalWidget
                size={isNarrow ? 'card' : 'overlay'}
                title={previewTitle}
                description={previewDesc}
                currentAmount={previewCurrent}
                targetAmount={previewTarget}
                donationCount={previewCount}
                isExpired={hasGoal && !!goal?.isExpired}
                isActive={isActive}
                theme={theme}
                animateKey={demoTick}
                showStatus
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={simulateDonate}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-600 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 dark:border-blue-400 dark:text-blue-400 dark:hover:bg-slate-700"
            >
              <Sparkles size={16} />
              Xem thử hiệu ứng
            </button>
            <p className="text-xs text-gray-500 dark:text-gray-400 sm:text-right">
              Đổi giao diện không cần chép lại link, thanh trên live tự cập nhật sau tối đa 1 phút.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <h2 className="text-sm">
            Link hiển thị mục tiêu ( Add Webpage URL trong phần mềm Stream )
          </h2>
          <div className="flex items-center gap-2">
            <div className="flex h-12 min-w-0 flex-1 items-center rounded-lg border border-[#C8CCD8] dark:border-slate-600 bg-[#EBECF2] dark:bg-slate-700 px-3 text-base text-[#A1A5B6] dark:text-slate-400">
              <span className="w-full truncate">{overlayUrl}</span>
            </div>
            <button
              type="button"
              onClick={copyOverlay}
              title="Sao chép link"
              className="flex h-12 shrink-0 items-center gap-1 rounded-lg bg-[#39539E] px-4 text-white"
            >
              <Copy size={16} />
              Sao chép
            </button>
          </div>
        </div>

        <div className="flex justify-between gap-3">
          <button
            type="button"
            onClick={openPreview}
            className="rounded-lg bg-[#39539E] px-4 py-2 text-base text-white"
          >
            Xem thử
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-blue-500 px-4 py-2 text-base text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? 'Đang lưu…' : 'Lưu mục tiêu'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DonateGoalSetting;
